import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Angular Material
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Third-party
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

// App imports
import { ServerStatusService } from '../../services/server-status.service';
import { ServerHealthStatus } from '../../Models/server-status.model';

/**
 * Server Status Indicator Component
 *
 * A compact, standalone indicator that displays the current backend server health status.
 * Automatically polls the health endpoint every 60 seconds (configurable).
 *
 * Features:
 * - Color-coded status icon (green/yellow/red)
 * - Tooltip with status description
 * - Click to navigate to full dashboard
 * - Loading state during initial fetch
 * - Auto-refresh with configurable interval
 *
 * Usage:
 * ```html
 * <ri-server-status-indicator></ri-server-status-indicator>
 *
 * <!-- With custom interval (30 seconds) -->
 * <ri-server-status-indicator [pollInterval]="30000"></ri-server-status-indicator>
 *
 * <!-- Without navigation (indicator only) -->
 * <ri-server-status-indicator [navigateOnClick]="false"></ri-server-status-indicator>
 * ```
 *
 * @requires CanManageUserRoles permission (for dashboard access)
 */
@Component({
    selector: 'ri-server-status-indicator',
    imports: [
        CommonModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        TranslatePipe
    ],
    templateUrl: './server-status-indicator.component.html',
    styleUrls: ['./server-status-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServerStatusIndicatorComponent implements OnInit, OnDestroy {
  /** Polling interval in milliseconds (default: 60000 = 1 minute) */
  @Input() pollInterval = 60000;

  /** Whether clicking the indicator navigates to the dashboard */
  @Input() navigateOnClick = true;

  /** Route to navigate to when clicked */
  @Input() dashboardRoute = '/server-status';

  /** Current server health status */
  status: ServerHealthStatus = 'Unknown';

  /** Timestamp of last successful health check */
  lastChecked: Date | null = null;

  /** Whether the initial health check is still loading */
  isLoading = true;

  /** Whether there was an error fetching status */
  hasError = false;

  private destroy$ = new Subject<void>();

  constructor(
    private serverStatusService: ServerStatusService,
    private router: Router,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Start polling for health status
   */
  private startPolling(): void {
    this.serverStatusService
      .pollHealthStatus(this.pollInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.status = response.status;
          this.lastChecked = new Date(response.timestamp);
          this.isLoading = false;
          this.hasError = response.status === 'Unknown';
          this.cdr.markForCheck();
        },
        error: () => {
          this.status = 'Unknown';
          this.isLoading = false;
          this.hasError = true;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Get CSS class for current status
   */
  get statusClass(): string {
    return this.serverStatusService.getStatusColorClass(this.status);
  }

  /**
   * Get Material icon for current status
   */
  get statusIcon(): string {
    return this.serverStatusService.getStatusIcon(this.status);
  }

  /**
   * Get translated tooltip text
   */
  get tooltip(): string {
    const statusKey = `SERVER_STATUS.${this.status.toUpperCase()}`;
    const statusText = this.translate.instant(statusKey);

    if (this.lastChecked) {
      const lastCheckedKey = this.translate.instant('SERVER_STATUS.LAST_CHECKED');
      const timeStr = this.lastChecked.toLocaleTimeString();
      return `${statusText}\n${lastCheckedKey}: ${timeStr}`;
    }

    return statusText;
  }

  /**
   * Handle indicator click
   */
  onClick(): void {
    if (this.navigateOnClick) {
      this.router.navigate([this.dashboardRoute]);
    }
  }

  /**
   * Manual refresh trigger
   */
  refresh(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.serverStatusService.getHealthCheck().subscribe({
      next: (response) => {
        this.status = response.status;
        this.lastChecked = new Date(response.timestamp);
        this.isLoading = false;
        this.hasError = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.status = 'Unknown';
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
      }
    });
  }
}
